import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const SCENES = [
  { src: "/demo/01-panoramica.png", title: "La tua dashboard", text: "Categorie, piatti, piano attivo e statistiche a colpo d'occhio." },
  { src: "/demo/02-menu.png", title: "Costruttore menù", text: "Aggiungi categorie e piatti, trascina per riordinare, attiva l'AI per descrizioni e allergeni." },
  { src: "/demo/03-daily.png", title: "Menù del giorno", text: "Cambia le proposte del giorno in pochi secondi: il QR resta lo stesso." },
  { src: "/demo/04-qr.png", title: "Il tuo QR Code", text: "Scarica il QR, stampa i cartoncini per i tavoli, condividi il link sui social." },
  { src: "/demo/06-pubblico.png", title: "Il menù per i clienti", text: "Lingue, filtri allergeni, ordini da asporto e pagamento al tavolo.", phone: true },
];
const SCENE_MS = 6000;

export const DemoVideoDialog = ({ open, onOpenChange }) => {
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);

  useEffect(() => { if (open) { setI(0); setPlaying(true); } }, [open]);
  useEffect(() => {
    if (!open || !playing) return;
    const t = setTimeout(() => setI((x) => (x + 1) % SCENES.length), SCENE_MS);
    return () => clearTimeout(t);
  }, [i, open, playing]);

  const s = SCENES[i];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden bg-gray-950 border-white/10 text-white [&>button]:hidden" data-testid="demo-video-dialog">
        <div className="relative aspect-video bg-gray-950">
          <AnimatePresence mode="wait">
            <motion.div key={s.src} initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="absolute inset-0 flex items-center justify-center p-6 sm:p-10">
              {s.phone ? (
                <div className="h-full rounded-[2rem] bg-gray-900 p-2 ring-1 ring-white/10 shadow-2xl">
                  <img src={s.src} alt={s.title} className="h-full rounded-[1.6rem] object-cover object-top" />
                </div>
              ) : (
                <img src={s.src} alt={s.title} className="max-h-full w-auto rounded-xl ring-1 ring-white/10 shadow-2xl" />
              )}
            </motion.div>
          </AnimatePresence>
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-gray-950 via-gray-950/85 to-transparent pt-24 pb-6 px-6 sm:px-10">
            <AnimatePresence mode="wait">
              <motion.div key={s.title} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.4 }}>
                <p className="text-[#8fd39c] text-xs tracking-[0.25em] uppercase mb-1">{i + 1} / {SCENES.length}</p>
                <h3 className="font-display font-bold text-2xl sm:text-3xl">{s.title}</h3>
                <p className="text-gray-300 text-sm sm:text-base mt-1 max-w-2xl">{s.text}</p>
              </motion.div>
            </AnimatePresence>
            <div className="mt-5 flex items-center gap-3">
              <button onClick={() => setPlaying((p) => !p)} className="h-9 w-9 rounded-full bg-white text-gray-900 flex items-center justify-center hover:scale-105 transition-transform" data-testid="demo-play-pause">
                {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
              </button>
              <div className="flex-1 flex gap-1.5">
                {SCENES.map((sc, k) => (
                  <button key={sc.src} onClick={() => { setI(k); setPlaying(true); }} className="h-1.5 flex-1 rounded-full bg-white/20 overflow-hidden" data-testid={`demo-scene-${k}`}>
                    {k < i && <div className="h-full w-full bg-[#5aa469]" />}
                    {k === i && <motion.div key={`${k}-${playing}`} initial={{ width: 0 }} animate={{ width: playing ? "100%" : undefined }} transition={{ duration: SCENE_MS / 1000, ease: "linear" }} className="h-full bg-[#5aa469]" />}
                  </button>
                ))}
              </div>
              <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} className="text-white hover:bg-white/10" data-testid="demo-close"><X className="h-5 w-5" /></Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
